"use client";

import { useCallback, useLayoutEffect, useRef } from "react";
import styles from "./Textarea.module.scss";
import classNames from "classnames";
import { Fieldset } from "../fieldset";
import type { TextareaTypes } from "./Textarea.types";

const Textarea = ({
  id,
  name,
  label,
  disabled = false,
  readOnly = false,
  required = false,
  className = "",
  placeholder,
  defaultValue = "",
  value,
  startContent = null,
  endContent = null,
  autoFocus = false,
  maxLength,
  rows = 1,
  spellCheck,
  onChange,
  size = "medium",
  labelPlacement = "outside",
  variant = "primary",
  error,
}: TextareaTypes) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const syncHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    syncHeight();
  }, [value, rows, syncHeight]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (readOnly) return;
    const next = e.target.value;
    if (onChange) onChange(next);
    queueMicrotask(() => syncHeight());
  };

  return (
    <Fieldset error={error}>
      <div className={classNames(styles["comp"], className)}>
        {label && labelPlacement === "outside" && (
          <label className={styles["comp__label"]} htmlFor={id}>
            <span>{label}</span>
            {required && <span>*</span>}
          </label>
        )}

        <div
          className={classNames(
            styles["comp__container"],
            styles[`comp__container--variant-${error ? "danger" : variant}`],
            styles[`comp__container--size-${size}`],
            disabled && styles["comp__container--disabled"]
          )}
        >
          {startContent && startContent}

          <textarea
            ref={textareaRef}
            id={id}
            name={name ?? id}
            rows={rows}
            disabled={disabled}
            readOnly={readOnly}
            autoFocus={autoFocus}
            maxLength={maxLength}
            spellCheck={spellCheck}
            onChange={handleChange}
            placeholder={placeholder}
            {...(value !== undefined ? { value } : { defaultValue })}
            className={styles["comp__textarea"]}
            style={
              rows > 1
                ? {
                    minHeight: `calc(${rows} * ${
                      size === "large" ? "24px" : "20px"
                    })`,
                  }
                : undefined
            }
          />

          {endContent && endContent}
        </div>
      </div>
    </Fieldset>
  );
};

export { Textarea };
